/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { dashboardsUpdate } from "../funcs/dashboardsUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Dashboard } from "../models/components/dashboard.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateDashboardRequest,
  UpdateDashboardSecurity,
} from "../models/operations/updatedashboard.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateDashboardMutationVariables = {
  request: UpdateDashboardRequest;
  security?: UpdateDashboardSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateDashboardMutationData = Dashboard;

export type UpdateDashboardMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateDashboard dashboards
 *
 * @remarks
 * Rename a dashboard or change its description. Its creator can; editing someone else's needs project write access.
 */
export function useUpdateDashboardMutation(
  options?: MutationHookOptions<
    UpdateDashboardMutationData,
    UpdateDashboardMutationError,
    UpdateDashboardMutationVariables
  >,
): UseMutationResult<
  UpdateDashboardMutationData,
  UpdateDashboardMutationError,
  UpdateDashboardMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateDashboardMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateDashboard(): MutationKey {
  return ["@gram/client", "dashboards", "update"];
}

export function buildUpdateDashboardMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateDashboardMutationVariables,
  ) => Promise<UpdateDashboardMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateDashboard(),
    mutationFn: function updateDashboardMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateDashboardMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(dashboardsUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
